// [목적] 페이지 상단바 — breadcrumb + title + 우측 액션 슬롯
// [이유] docs/플리오랩/web/shell.jsx TopBar 포팅. 페이지마다 컨텍스트 노출.
//         W3-2(design-alignment): bgcolor 하드코딩 rgba(250,249,247,.85) 제거 →
//         paper[1] 토큰 + MUI alpha 유틸(theme.palette.alpha)로 토큰 기반 배경 처리.
//         shell.jsx 시안: background:'rgba(250,249,247,.85)', backdropFilter:'blur(8px)'
//         paper[1] = #FAF9F7(편지지 톤) → alpha 85% = rgba(250,249,247,.85) 와 동일하므로 토큰으로 대체 가능.
// [사이드 이팩트] sticky top:0 + backdrop-filter blur. 모바일에서는 burger 버튼 노출.
//                 bgcolor 변경 없음(동일 색, 표현만 토큰 기반으로 전환).
// [수정 시 고려사항] 타이틀이 길어질 경우 ellipsis 처리. right 슬롯에 IconButton 등 자유 배치.
//                   alpha 값(0.85) 변경 시 UX 영향 검토 — backdropFilter blur 와 짝으로 운용.

import { Box, IconButton, Stack, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { tokens } from '@gangwoncanvas/ui';
import type { ReactNode } from 'react';

import { NotificationBell } from '../notifications/NotificationBell.js';

const c = tokens.color;
const L = 'light' as const;

/**
 * [목적] TopBar 배경색 — paper[1] 토큰 기반 반투명 배경
 * [이유] W3-2: rgba() 하드코딩 제거. paper[1]=#FAF9F7 을 MUI alpha 유틸로 0.85 적용 →
 *         rgba(250,249,247,0.85) 와 동일한 시각 결과이나 토큰 참조로 드리프트 방지.
 * [사이드 이팩트] 없음 (동일 색상 값)
 * [수정 시 고려사항] alpha 계수(0.85) 는 backdropFilter blur 효과와 짝 — 함께 조정.
 */
const TOPBAR_BG = alpha(c.paper[1][L], 0.85);

interface TopBarProps {
  title: string;
  breadcrumb?: string;
  right?: ReactNode;
  mobile?: boolean;
  onBurger?: () => void;
  /**
   * [목적] 알림 벨 숨김 스위치 (기본 노출)
   * [이유] 2026-09-20 notification-bell — 알림 자체를 보여주는 화면(/notifications)에서는 헤더 벨이
   *        같은 기능을 중복 제공해 혼란스럽다. 그런 화면만 false 로 끈다.
   * [수정 시 고려사항] 기본값 true 를 바꾸지 말 것 — TopBar 를 쓰는 신규 페이지가 조용히 알림 진입점을
   *                   잃는다(누락을 눈치채기 어려운 종류의 회귀).
   */
  showNotifications?: boolean;
}

export function TopBar({
  title,
  breadcrumb,
  right,
  mobile,
  onBurger,
  showNotifications = true,
}: TopBarProps): JSX.Element {
  return (
    <Box
      component="header"
      sx={{
        height: 54,
        px: '22px',
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
        borderBottom: `1px solid ${c.line.base[L]}`,
        bgcolor: TOPBAR_BG,
        backdropFilter: 'blur(8px)',
        position: 'sticky',
        top: 0,
        zIndex: 10,
        flexShrink: 0,
      }}
    >
      {mobile && (
        <IconButton
          onClick={onBurger}
          aria-label="사이드바 열기"
          size="small"
          sx={{ width: 30, height: 30, color: c.ink.high[L], fontSize: 18 }}
        >
          ☰
        </IconButton>
      )}
      <Stack sx={{ flex: 1, minWidth: 0 }} spacing={0}>
        {breadcrumb && (
          <Typography
            sx={{
              fontSize: 10.5,
              color: c.ink.low[L],
              fontFamily: 'JetBrains Mono, ui-monospace, monospace',
            }}
          >
            {breadcrumb}
          </Typography>
        )}
        <Typography
          component="h1"
          sx={{
            fontSize: 15,
            fontWeight: 700,
            color: c.ink.high[L],
            letterSpacing: '-0.3px',
            m: 0,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {title}
        </Typography>
      </Stack>
      {right}
      {/* [목적] 공통 헤더 알림 진입점 — TopBar 를 쓰는 모든 페이지에 동일 위치로 노출
          [이유] 2026-09-20 notification-bell: 알림을 보려면 사이드바 → 전체 페이지 이동이 유일한
                 경로였다. 보던 화면을 떠나지 않고 훑을 수 있게 헤더에 벨 + 드롭다운을 둔다.
          [사이드 이팩트] right 슬롯 뒤(가장 오른쪽)에 배치 — 페이지별 액션 버튼과 자리 다툼이 없다.
          [수정 시 고려사항] 위치를 right 앞으로 옮기면 페이지마다 벨 x좌표가 달라져 근육 기억이 깨진다. */}
      {showNotifications && <NotificationBell />}
    </Box>
  );
}
